import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Box, Typography, TextField, Alert, FormLabel } from '@mui/material'
import type { UUID } from 'crypto'
import { useOnboardingV2 } from '../OnboardingContext'
import { useStep } from '../hooks/useStep'
import { useToken } from '../hooks/useToken'
import { useUpdateProfileText } from '../hooks/useUpdateProfileText'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { stepHeadingSx, stepDescriptionSx, inputSx, textFieldSx, formLabelSx } from '../styles'
import { COLORS } from '../theme'

interface ProfileSection {
  title: string
  content: string
}

function parseProfileSections(text: string): ProfileSection[] {
  if (!text.trim()) return []
  const parts = text.split(/^## /m).filter(Boolean)
  return parts.map(part => {
    const newlineIdx = part.indexOf('\n')
    if (newlineIdx === -1) return { title: part.trim(), content: '' }
    return {
      title: part.slice(0, newlineIdx).trim(),
      content: part.slice(newlineIdx + 1).trim(),
    }
  })
}

function recombineSections(sections: ProfileSection[]): string {
  return sections.map(s => `## ${s.title}\n${s.content}`).join('\n\n')
}

function sectionId(index: number): string {
  return `profile-section-${index}`
}

export default function ProfileReview() {
  const { orgUuid } = useOnboardingV2()
  const { getToken } = useToken()
  const { user, token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''
  const updateProfileText = useUpdateProfileText()

  const { orgData, isLoading } = useCurrentUserOrgs(
    token,
    user,
    orgUuid ? [orgUuid as UUID] : [],
  )
  const org = orgData.find(o => o.uuid === orgUuid) ?? null

  const initialSections = useMemo(
    () => parseProfileSections(org?.profile_text ?? ''),
    [org?.profile_text],
  )

  const [sections, setSections] = useState<ProfileSection[]>(initialSections)
  const [isDirty, setIsDirty] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')

  // Sync sections once when org data loads asynchronously
  const initializedRef = useRef(initialSections.length > 0)
  useEffect(() => {
    if (!initializedRef.current && initialSections.length > 0) {
      initializedRef.current = true
      setSections(initialSections)
    }
  }, [initialSections])

  const saveProfile = useCallback(async () => {
    if (!isDirty || !orgUuid) return
    setSaveStatus('saving')
    try {
      const t = await getToken()
      await updateProfileText.mutateAsync({
        token: t,
        orgUuid: orgUuid as UUID,
        profileText: recombineSections(sections),
        force: true,
      })
      setIsDirty(false)
      setSaveStatus('saved')
    } catch {
      setSaveStatus('error')
    }
  }, [isDirty, orgUuid, sections, getToken, updateProfileText])

  useStep({
    isValid: true,
    beforeNext: saveProfile,
  })

  const handleSectionChange = useCallback(
    (index: number, field: 'title' | 'content', value: string) => {
      setSections(prev =>
        prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
      )
      setIsDirty(true)
      setSaveStatus('idle')
    },
    [],
  )

  return (
    <Box>
      <Typography variant="h5" sx={stepHeadingSx}>
        Review your profile
      </Typography>
      <Typography sx={stepDescriptionSx}>
        We have generated a profile for your organisation. Review each section below and make any
        edits you would like.
      </Typography>

      {saveStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveStatus('idle')}>
          Failed to save changes. Please try again.
        </Alert>
      )}
      {saveStatus === 'saved' && (
        <Typography variant="body2" sx={{ color: COLORS.success, mb: 2 }}>
          Changes saved
        </Typography>
      )}
      {saveStatus === 'saving' && (
        <Typography variant="body2" sx={{ color: COLORS.secondary, mb: 2 }}>
          Saving...
        </Typography>
      )}

      {sections.map((section, idx) => (
        <Box key={idx} sx={{ mb: 3 }}>
          <FormLabel
            htmlFor={sectionId(idx)}
            sx={formLabelSx}
          >
            {section.title}
          </FormLabel>
          <TextField
            id={sectionId(idx)}
            fullWidth
            multiline
            minRows={8}
            value={section.content}
            onChange={e => handleSectionChange(idx, 'content', e.target.value)}
            onBlur={saveProfile}
            sx={textFieldSx}
            slotProps={{ input: { sx: inputSx } }}
          />
        </Box>
      ))}

      {sections.length === 0 && !isLoading && (
        <Box
          sx={{
            border: `2px dashed ${COLORS.disabledIcon}`,
            backgroundColor: COLORS.hoverBg,
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ color: COLORS.secondary }}>
            No profile content found. Please go back and try generating your profile again.
          </Typography>
        </Box>
      )}
    </Box>
  )
}
